/**
 * El marco compartido de los desplegables del encabezado: Chats y Notificaciones.
 *
 * POR QUÉ UNO SOLO. Los dos son la misma cosa —un panel con título, una lista larga que scrollea y
 * un enlace al final— y hasta ahora eran dos cosas distintas: chats era un panel propio y
 * notificaciones era un `ActionList` de Polaris, que no está pensado para filas con foto, hora y
 * punto de no leído. Se notaba: uno se veía como una app de mensajería y el otro como un menú
 * contextual estirado.
 *
 * Con un marco compartido, los dos abren igual, miden igual y scrollean igual. Y lo importante:
 * cuando haya que tocar el alto o el ancho, se toca una vez. Messenger hace exactamente esto — su
 * panel de chats y el de notificaciones son el mismo contenedor con distinto contenido.
 *
 * 🪤 EL ALTO SE MIDE CONTRA LA VENTANA, NO EN PÍXELES FIJOS. Un panel de alto fijo se ve bien en un
 * monitor y se corta abajo en una laptop de 768px, donde además el encabezado ya se comió 56px. Por
 * eso el máximo es un `min()` entre un tope cómodo y lo que realmente queda de pantalla.
 */

import type { ReactNode } from 'react';
import { Text, Popover, Spinner } from '@/lib/polaris';
import { Z_DOCK } from '@/contexts/WaChatDockContext';

/**
 * 🪤 SIN ESTO, EL PANEL SE ABRE DETRÁS DE LOS CHATS ACOPLADOS. El popover de Polaris usa una capa
 * baja y las ventanas del dock están en Z_DOCK: con dos o tres chats abiertos, el desplegable
 * quedaba tapado. Vive en el marco compartido y no en cada panel justamente para que el próximo
 * que se agregue no repita el bug — con notificaciones ya pasó.
 */
const Z_PANEL = Z_DOCK + 10;

/**
 * Para lo que se abre DENTRO del panel: el menú de una fila, por ejemplo.
 *
 * 🪤 Y ES LA SEGUNDA VUELTA DEL MISMO BUG, un nivel más adentro. El panel ya subía por encima de los
 * chats acoplados, pero un menú abierto desde una de sus filas volvía a la capa por defecto de
 * Polaris — o sea, DEBAJO del panel que lo contiene. El síntoma engaña: la flecha responde, el menú
 * se abre, y no se ve nada. Parece que el click no hizo nada.
 *
 * Se exporta para que ningún menú tenga que adivinar el número.
 */
export const Z_SOBRE_EL_PANEL = Z_PANEL + 10;

/**
 * Tope en pantallas grandes. Alto a propósito: el panel llega casi hasta abajo, como el de
 * Messenger — la gracia es ver muchos chats de un vistazo, no cuatro y scrollear.
 */
const ALTO_MAXIMO = 760;

/**
 * Lo que se reserva arriba y abajo: el encabezado (56px) más un respiro para que el panel no
 * quede pegado al borde de la ventana.
 */
const RESERVA_VERTICAL = 80;

export interface PanelDelEncabezadoProps {
  abierto: boolean;
  onCerrar: () => void;
  /** El botón del encabezado. Polaris lo clona, así que tiene que ser un elemento. */
  activador: React.ReactElement;
  titulo: string;
  /** Acción secundaria del encabezado del panel, tipo "Marcar todas como leídas". */
  accion?: { texto: string; onClick: () => void };
  /** Buscador u otro control fijo debajo del título; no scrollea con la lista. */
  encabezadoExtra?: ReactNode;
  /** Pestañas de filtro. Van en el marco porque los dos paneles las tienen. */
  pestanias?: Array<{ id: string; texto: string; contador?: number }>;
  pestaniaActiva?: string;
  onPestania?: (id: string) => void;
  cargando?: boolean;
  /** Estado vacío. Se muestra cuando no hay `children`. */
  vacio?: ReactNode;
  /** Enlace del pie: "Ver todos los chats", "Ver todas las notificaciones". */
  pie?: { texto: string; onClick: () => void };
  /**
   * Capa. Por defecto queda sobre las ventanas acopladas — ver la trampa abajo. Solo se pasa si
   * algún panel necesitara otra cosa.
   */
  zIndexOverride?: number;
  children?: ReactNode;
}

export function PanelDelEncabezado({
  abierto, onCerrar, activador, titulo, accion, encabezadoExtra,
  pestanias, pestaniaActiva, onPestania,
  cargando, vacio, pie, zIndexOverride = Z_PANEL, children,
}: PanelDelEncabezadoProps) {
  const hayContenido = Array.isArray(children) ? children.length > 0 : !!children;

  return (
    <Popover
      active={abierto}
      activator={activador}
      onClose={onCerrar}
      preferredAlignment="right"
      autofocusTarget="none"
      zIndexOverride={zIndexOverride}
      /*
       * 🪤 ACÁ ESTABA EL DOBLE SCROLL. El contenedor de Polaris trae `max-height: 500px` y
       * `max-width: 400px` propios: nuestro panel pedía más alto, se pasaba, y terminaban
       * scrolleando DOS cajas — la de afuera arrastraba el título y el buscador, que tienen que
       * quedarse quietos. `fluidContent` saca esos topes y deja que el alto lo decida un solo
       * lugar: el `min()` de abajo.
       */
      fluidContent
      /*
       * 🪤 SIN ESTO, UN MENÚ DENTRO DEL PANEL NO SE PUEDE ABRIR. Este panel es un Popover, y cuando
       * una fila abre el suyo —el menú de un chat, por ejemplo—, Polaris lo dibuja en otro portal:
       * para el panel de afuera ese click cae FUERA, así que se cierra y se lleva puesto al de
       * adentro. El síntoma es exacto y desconcertante: tocás la flecha y no pasa nada.
       *
       * Esta propiedad existe justamente para superposiciones anidadas. Va acá, en el panel, y no en
       * cada menú: es el de afuera el que tiene que dejar de cerrarse.
       */
      preventCloseOnChildOverlayClick
    >
      <div style={{
        width: 380, maxWidth: '92vw',
        display: 'flex', flexDirection: 'column',
        /*
         * 🪤 EL ALTO SE MIDE CONTRA LA VENTANA, NO EN PÍXELES FIJOS. Un panel de alto fijo se ve
         * bien en un monitor y se corta abajo en una laptop, donde el encabezado ya se comió 56px.
         * El `min()` deja que mande el que sea menor: el tope cómodo o lo que hay de pantalla.
         */
        maxHeight: `min(${ALTO_MAXIMO}px, calc(100vh - ${RESERVA_VERTICAL}px))`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '14px 16px 8px', flexShrink: 0,
        }}>
          <Text as="h2" variant="headingLg">{titulo}</Text>
          {accion && (
            <button
              type="button"
              onClick={accion.onClick}
              style={{
                background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                color: 'var(--p-color-text-emphasis)', fontSize: 13, fontWeight: 600,
              }}
            >
              {accion.texto}
            </button>
          )}
        </div>

        {encabezadoExtra && (
          <div style={{ padding: '0 12px 8px', flexShrink: 0 }}>{encabezadoExtra}</div>
        )}

        {pestanias && pestanias.length > 0 && (
          <div style={{ display: 'flex', gap: 6, padding: '0 12px 10px', flexShrink: 0 }}>
            {pestanias.map((t) => {
              const activa = t.id === pestaniaActiva;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onPestania?.(t.id)}
                  aria-pressed={activa}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '5px 12px', borderRadius: 999, border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: 600,
                    background: activa ? 'var(--p-color-bg-surface-brand-selected)' : 'var(--p-color-bg-surface-secondary)',
                    color: activa ? 'var(--p-color-text-emphasis)' : 'var(--p-color-text-secondary)',
                  }}
                >
                  {t.texto}
                  {t.contador ? (
                    <span style={{ fontSize: 11, opacity: 0.8 }}>{t.contador > 99 ? '99+' : t.contador}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}

        {/* La lista es lo único que scrollea: el título y el pie quedan siempre a la vista. */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 4 }}>
          {cargando && !hayContenido ? (
            <div style={{ padding: '32px 0', display: 'flex', justifyContent: 'center' }}>
              <Spinner accessibilityLabel={`Cargando ${titulo.toLowerCase()}`} size="small" />
            </div>
          ) : hayContenido ? children : (
            <div style={{ padding: '32px 20px', textAlign: 'center' }}>{vacio}</div>
          )}
        </div>

        {pie && (
          <div style={{ borderTop: '1px solid var(--p-color-border)', flexShrink: 0 }}>
            <button
              type="button"
              onClick={pie.onClick}
              style={{
                width: '100%', padding: '12px', background: 'transparent', border: 'none',
                cursor: 'pointer', color: 'var(--p-color-text-emphasis)', fontWeight: 600, fontSize: 13,
              }}
            >
              {pie.texto}
            </button>
          </div>
        )}
      </div>
    </Popover>
  );
}

/**
 * Una fila del panel. La comparten chats y notificaciones porque tienen la misma anatomía: algo
 * redondo a la izquierda, dos líneas de texto al medio, hora y punto de no leído a la derecha.
 */
export function FilaDelPanel({
  avatar, titulo, detalle, hora, sinLeer, onClick, acciones, completo = false,
}: {
  avatar: ReactNode;
  titulo: string;
  detalle: ReactNode;
  hora?: string;
  /** `true` pinta el punto; un número pinta el globito con la cuenta. */
  sinLeer?: boolean | number;
  onClick: () => void;
  /**
   * Controles al final de la fila — un menú, por ejemplo.
   *
   * 🪤 VAN AFUERA DEL `<button>`, no adentro. La fila entera era un botón, y un botón dentro de otro
   * es HTML inválido: el navegador lo desarma y el de adentro deja de recibir clicks. Por eso ahora
   * la fila es un contenedor con el botón adentro y las acciones al lado, hermanas.
   */
  acciones?: ReactNode;
  /** El texto entero, sin cortar: en una página (todas las notificaciones) hay lugar; en el panel, no. */
  completo?: boolean;
}) {
  const destacado = sinLeer === true || (typeof sinLeer === 'number' && sinLeer > 0);

  // El hover vive en el contenedor y no en el botón: si viviera en el botón, pasar el mouse por el
  // menú apagaría el resaltado justo cuando vas a usarlo.
  const resaltar = (e: React.MouseEvent<HTMLDivElement>, encendido: boolean) => {
    e.currentTarget.style.background = encendido ? 'var(--p-color-bg-surface-hover)' : 'transparent';
  };

  return (
    <div
      className="fila-panel"
      // `relative` para que las acciones se posicionen contra la fila y no roben ancho.
      style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}
      onMouseEnter={(e) => resaltar(e, true)}
      onMouseLeave={(e) => resaltar(e, false)}
    >
      <style>{`
        .fila-panel-acciones { opacity: 0; transition: opacity 0.15s; }
        .fila-panel:hover .fila-panel-acciones,
        .fila-panel:focus-within .fila-panel-acciones { opacity: 1; }
        /* Y mientras SU menú está abierto, aunque el mouse se haya ido: una flecha que se
           desvanece con el menú desplegado abajo se ve como un error. */
        .fila-panel-acciones:has([data-menu-abierto="true"]) { opacity: 1; }
      `}</style>
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%',
        padding: '10px 16px', background: 'transparent', border: 'none',
        textAlign: 'left', cursor: 'pointer', minWidth: 0, flex: 1,
      }}
    >
      <div style={{ flexShrink: 0, display: 'flex' }}>{avatar}</div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <Text as="p" variant="bodyMd" fontWeight={destacado ? 'semibold' : 'regular'} truncate={!completo}>
          {titulo}
        </Text>
        <Text as="p" variant="bodySm" tone="subdued" truncate={!completo}>{detalle}</Text>
      </div>

      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
        gap: 5, flexShrink: 0, minWidth: 34,
      }}>
        {hora && <Text as="span" variant="bodySm" tone="subdued">{hora}</Text>}
        {typeof sinLeer === 'number' && sinLeer > 0 ? (
          <span
            aria-label={`${sinLeer} sin leer`}
            style={{
              minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
              background: '#25D366', color: '#fff', fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
            }}
          >
            {sinLeer > 99 ? '99+' : sinLeer}
          </span>
        ) : sinLeer === true ? (
          <span
            aria-label="Sin leer"
            style={{
              width: 10, height: 10, borderRadius: '50%',
              background: 'var(--p-color-bg-fill-brand)',
            }}
          />
        ) : null}
      </div>
    </button>
      {acciones ? (
        /*
         * Se muestra al pasar el mouse, como en la lista del módulo de Chats. Una flecha por fila
         * siempre visible convierte una lista de vistazo en una grilla de controles: lo que uno
         * viene a leer son los mensajes, no las opciones.
         *
         * 🪤 VA ENCIMA, NO AL LADO. Como hermana en el flex se comía ~36px de ancho en TODAS las
         * filas, también en las que no la muestran: en un panel de 380px eso apretó los nombres y
         * las vistas previas. El founder lo vio enseguida — «ocupa espacio tipo columna y hace que
         * los chats reduzcan su ancho».
         *
         * Superpuesta, la fila mide lo mismo con y sin flecha. Al aparecer tapa la hora, que es lo
         * que hace WhatsApp Web: mientras elegís una opción, la hora no es lo que estás mirando.
         *
         * 🪤 Y `opacity`, no `display`: con `display` el contenido se recalcula al entrar el mouse y
         * la lista "salta" mientras la recorrés.
         */
        <div
          className="fila-panel-acciones"
          style={{
            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
            display: 'flex', alignItems: 'center',
          }}
        >
          {acciones}
        </div>
      ) : null}
    </div>
  );
}
